/**
 * CO-VRD Theme — Modesty Guide JS
 * ================================================================
 * Handles:
 *   1. FAQ Accordion — single-open (optional) + smooth animation
 *   2. Quick View Modal — fetch modesty guide page into overlay
 *      from PDP trigger link
 *   3. Scroll-to-section — anchor link jumps (#opacity-scale, etc.)
 *
 * Dependencies:
 *   - window.trapFocus()   from global.js
 *   - window.releaseFocus() from global.js
 *
 * DOM hooks:
 *   data-modesty-accordion    — accordion container
 *   data-accordion-item       — each <details> element
 *   data-modesty-quickview    — the modal overlay element
 *   data-modesty-quickview-trigger — PDP link that opens modal
 *   data-modesty-quickview-close  — close button in modal
 *   data-modesty-quickview-body   — content insertion point
 * ================================================================
 */

(function () {
  'use strict';

  /* ============================================
     §1  FAQ Accordion — exclusive open
     Only one item open at a time per accordion.
     Uses native <details> for no-JS fallback.
     ============================================ */

  var accordions = document.querySelectorAll('[data-modesty-accordion]');

  accordions.forEach(function (accordion) {
    var items = accordion.querySelectorAll('[data-accordion-item]');

    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (item.open) {
          /* Close all siblings */
          items.forEach(function (sibling) {
            if (sibling !== item && sibling.open) {
              sibling.open = false;
            }
          });
        }
      });
    });
  });


  /* ============================================
     §2  Quick View Modal
     Fetches the modesty guide page content via
     Shopify section rendering API and displays
     it in a modal overlay on the PDP.
     ============================================ */

  var modal       = document.querySelector('[data-modesty-quickview]');
  var closeBtn    = modal ? modal.querySelector('[data-modesty-quickview-close]') : null;
  var overlay     = modal ? modal.querySelector('.modesty-quickview__overlay') : null;
  var bodyEl      = modal ? modal.querySelector('[data-modesty-quickview-body]') : null;
  var triggers    = document.querySelectorAll('[data-modesty-quickview-trigger]');
  var returnFocus = null;
  var loaded      = false;

  function openModal(trigger) {
    if (!modal) return;
    returnFocus = trigger || document.activeElement;

    /* Show modal */
    modal.hidden = false;
    modal.offsetHeight; /* force reflow */
    modal.classList.add('modesty-quickview--open');
    document.body.style.overflow = 'hidden';

    /* Trap focus */
    if (window.trapFocus) window.trapFocus(modal, closeBtn);

    /* Load content if not cached */
    if (!loaded && bodyEl) {
      fetchGuideContent(trigger);
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('modesty-quickview--open');
    document.body.style.overflow = '';

    /* Release focus */
    if (window.releaseFocus) window.releaseFocus(modal);

    modal.addEventListener('transitionend', function handler() {
      if (!modal.classList.contains('modesty-quickview--open')) {
        modal.hidden = true;
      }
      modal.removeEventListener('transitionend', handler);
    });

    if (returnFocus) returnFocus.focus();
  }

  function fetchGuideContent(trigger) {
    var url = trigger ? trigger.getAttribute('href') : null;
    if (!url) return;

    /* Use Shopify's section rendering API for clean content */
    var sectionUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=main-page-modesty';

    bodyEl.innerHTML = '<div class="modesty-quickview__loading" style="text-align:center;padding:var(--space-8) 0;"><p class="caption text-tertiary">Loading guide\u2026</p></div>';

    fetch(sectionUrl)
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        bodyEl.innerHTML = html;
        loaded = true;

        /* Re-init accordions inside modal */
        var modalAccordions = bodyEl.querySelectorAll('[data-modesty-accordion]');
        modalAccordions.forEach(function (accordion) {
          var items = accordion.querySelectorAll('[data-accordion-item]');
          items.forEach(function (item) {
            item.addEventListener('toggle', function () {
              if (item.open) {
                items.forEach(function (sibling) {
                  if (sibling !== item && sibling.open) {
                    sibling.open = false;
                  }
                });
              }
            });
          });
        });
      })
      .catch(function (err) {
        console.error('[CO-VRD] Modesty guide load error:', err);
        bodyEl.innerHTML = '<p class="body-sm text-tertiary" style="text-align:center;padding:var(--space-8) 0;">Could not load the guide. <a href="' + (url || '#') + '">View the full page instead.</a></p>';
      });
  }

  /* Bind triggers */
  triggers.forEach(function (trigger) {
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      openModal(trigger);
    });
  });

  /* Bind close */
  if (closeBtn) closeBtn.addEventListener('click', closeModal);
  if (overlay) overlay.addEventListener('click', closeModal);

  /* Escape key */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal && modal.classList.contains('modesty-quickview--open')) {
      closeModal();
    }
  });


  /* ============================================
     §3  Smooth scroll to sections
     Handles hash links like #opacity-scale.
     ============================================ */

  var anchorLinks = document.querySelectorAll('a[href^="#opacity-scale"], a[href^="#length-guide"], a[href^="#coverage-guide"], a[href^="#fit-coverage-faq"]');

  anchorLinks.forEach(function (link) {
    link.addEventListener('click', function (e) {
      var target = document.querySelector(link.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

})();
